import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, LogOut, Mail } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthLayout } from '@/components/layout/auth-layout';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, Input, Skeleton, Textarea } from '@/components/ui/primitives';
import { api, ApiError, errorMessage } from '@/lib/api';
import type { PortalTicketDetail } from '@/lib/types';
import { cn, formatDate, formatDateTime, formatRelative } from '@/lib/utils';
import { PortalStatus } from '@/pages/portal/portal-pages';

type GuestTicket = Omit<PortalTicketDetail, 'attachments'>;

/** Sitzung nur für diesen Tab (verfällt serverseitig nach 2 Stunden). */
const storageKey = (token: string) => `sp-guest-${token}`;
export function readSession(token: string) {
  try {
    return sessionStorage.getItem(storageKey(token));
  } catch {
    return null;
  }
}
function writeSession(token: string, value: string | null) {
  try {
    if (value) sessionStorage.setItem(storageKey(token), value);
    else sessionStorage.removeItem(storageKey(token));
  } catch {
    /* ohne Speicher gilt die Sitzung nur bis zum Neuladen */
  }
}

/** Link aus der E-Mail + Einmalcode an die hinterlegte Adresse; danach zeigt `children` den Vorgang. */
export function GuestCodeGate({
  base,
  token,
  noun,
  openLabel,
  children,
}: {
  base: string;
  token: string;
  noun: 'Anfrage' | 'Reparatur';
  openLabel: string;
  children: (session: string, close: () => void) => ReactNode;
}) {
  const [session, setSession] = useState<string | null>(() => readSession(token));
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);

  const info = useQuery({ queryKey: ['guest', base], queryFn: () => api<{ tenant: string; number: string; emailHint: string; locked: boolean }>(base), retry: false });
  const requestCode = useMutation({
    mutationFn: () => api<{ emailHint: string; validMinutes: number }>(`${base}/code`, { method: 'POST' }),
    onSuccess: (r) => setSentTo(r.emailHint),
  });
  const verify = useMutation({
    mutationFn: () => api<{ session: string }>(`${base}/verify`, { method: 'POST', body: { code } }),
    onSuccess: (r) => {
      writeSession(token, r.session);
      setSession(r.session);
      setCode('');
    },
  });
  const logout = useCallback(() => {
    writeSession(token, null);
    setSession(null);
    setSentTo(null);
  }, [token]);

  if (info.isError) {
    return (
      <AuthLayout title={noun} description="Dieser Link ist ungültig oder nicht mehr aktiv.">
        <p className="text-sm text-muted-foreground">Bitte verwenden Sie den Link aus der neuesten E-Mail oder melden Sie sich im Kundenportal an.</p>
        <Link to="/login" className="mt-4 inline-block text-sm text-primary hover:underline">
          Zum Kundenportal
        </Link>
      </AuthLayout>
    );
  }

  if (session) return <>{children(session, logout)}</>;

  const locked = info.data?.locked || [requestCode.error, verify.error].some((e) => e instanceof ApiError && e.status === 429 && e.code === 'LOCKED');
  return (
    <AuthLayout title={info.data ? `${noun} ${info.data.number}` : noun} description={info.data ? `${info.data.tenant} – zum Schutz Ihrer Daten bestätigen Sie bitte Ihre E-Mail-Adresse.` : undefined}>
      {!info.data ? (
        <Skeleton className="h-32" />
      ) : (
        <div className="grid gap-4">
          {locked && <Alert variant="destructive">Zu viele Fehlversuche. Bitte versuchen Sie es in 15 Minuten erneut.</Alert>}
          {requestCode.error && !locked && <Alert variant="destructive">{errorMessage(requestCode.error)}</Alert>}
          {!sentTo ? (
            <>
              <p className="text-sm text-muted-foreground">
                Wir senden Ihnen einen 6-stelligen Code an <span className="font-medium text-foreground">{info.data.emailHint}</span>.
              </p>
              <Button onClick={() => requestCode.mutate()} loading={requestCode.isPending} disabled={locked}>
                <Mail /> Code per E-Mail senden
              </Button>
            </>
          ) : (
            <form
              className="grid gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                verify.mutate();
              }}
            >
              <p className="text-sm text-muted-foreground">
                Der Code wurde an <span className="font-medium text-foreground">{sentTo}</span> gesendet und ist 15 Minuten gültig.
              </p>
              {verify.error && !locked && <Alert variant="destructive">{errorMessage(verify.error)}</Alert>}
              <Field label="Code (6 Ziffern)" htmlFor="guest-code">
                <Input
                  id="guest-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center font-mono text-2xl tracking-[0.5em]"
                  autoFocus
                />
              </Field>
              <Button type="submit" loading={verify.isPending} disabled={code.length !== 6 || locked}>
                <Lock /> {openLabel}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => requestCode.mutate()} loading={requestCode.isPending} disabled={locked}>
                Neuen Code senden
              </Button>
            </form>
          )}
        </div>
      )}
    </AuthLayout>
  );
}

/** Anfrage ohne Kundenkonto ansehen und beantworten: Link aus der E-Mail + Einmalcode an die hinterlegte Adresse. */
export function GuestTicketPage() {
  const { token = '' } = useParams<{ token: string }>();
  const base = `/public/ticket-access/${encodeURIComponent(token)}`;
  return (
    <GuestCodeGate base={base} token={token} noun="Anfrage" openLabel="Anfrage öffnen">
      {(session, close) => <GuestTicketView base={base} token={token} session={session} onExpired={close} />}
    </GuestCodeGate>
  );
}

function GuestTicketView({ base, token, session, onExpired }: { base: string; token: string; session: string; onExpired: () => void }) {
  const qc = useQueryClient();
  const [reply, setReply] = useState('');
  const headers = { 'x-guest-session': session };
  const key = ['guest-ticket', token, 'detail'];
  const { data: t, error } = useQuery({ queryKey: key, queryFn: () => api<GuestTicket>(`${base}/ticket`, { headers }), retry: false });
  const send = useMutation({
    mutationFn: () => api(`${base}/comments`, { method: 'POST', body: { body: reply }, headers }),
    onSuccess: () => {
      setReply('');
      qc.invalidateQueries({ queryKey: key });
      toast.success('Nachricht gesendet');
    },
    onError: (e) => (e instanceof ApiError && e.status === 401 ? onExpired() : toast.error(errorMessage(e))),
  });

  const expired = error instanceof ApiError && error.status === 401;
  useEffect(() => {
    if (expired) onExpired();
  }, [expired, onExpired]);
  if (expired) return null;
  if (!t) return <Skeleton className="m-8 h-96" />;

  return (
    <div className="min-h-dvh bg-gradient-to-b from-primary/8 via-background to-background px-4 py-8">
      <div className="mx-auto grid max-w-2xl gap-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm text-muted-foreground">{t.number}</span>
              <PortalStatus status={t.status} isClosed={t.isClosed} />
              <Badge variant="outline">{t.area.name}</Badge>
            </div>
            <h1 className="mt-1 text-xl font-semibold tracking-tight">{t.subject}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Eingegangen am {formatDateTime(t.createdAt)}
              {t.assigneeName && ` · Bearbeitet von ${t.assigneeName}`}
              {t.dueAt && ` · Voraussichtlich bis ${formatDate(t.dueAt)}`}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onExpired}>
            <LogOut /> Schließen
          </Button>
        </div>
        <Card>
          <CardContent className="whitespace-pre-wrap break-words pt-5 text-sm">{t.description}</CardContent>
        </Card>
        {t.messages.map((m) => (
          <div key={m.id} className={cn('rounded-xl border p-4', m.fromCustomer ? 'ml-6 border-primary/20 bg-primary/5' : 'mr-6 bg-card')}>
            <p className="mb-1 text-sm">
              <span className="font-medium">{m.fromCustomer ? (m.authorName ?? 'Sie') : (m.authorName ?? 'Service-Team')}</span>{' '}
              <span className="text-xs text-muted-foreground">{formatRelative(m.createdAt)}</span>
            </p>
            <p className="whitespace-pre-wrap break-words text-sm">{m.body}</p>
          </div>
        ))}
        {t.isClosed ? (
          <Alert>Diese Anfrage ist abgeschlossen. Für ein neues Anliegen schreiben Sie uns bitte eine neue E-Mail.</Alert>
        ) : (
          <Card>
            <CardContent className="pt-4">
              <Textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Ihre Nachricht an das Service-Team …" aria-label="Nachricht" />
              <div className="mt-2 flex justify-end">
                <Button onClick={() => send.mutate()} disabled={!reply.trim()} loading={send.isPending}>
                  Senden
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        {t.history && t.history.length > 0 && (
          <Card>
            <CardContent className="grid gap-1 pt-4 text-xs text-muted-foreground">
              {t.history.map((h, i) => (
                <p key={i}>
                  {formatDateTime(h.createdAt)} · {h.label}
                </p>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
